import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Space, Typography, message, Row, Col, Tabs, Tag, Badge,
  Alert
} from 'antd';
import {
  ReloadOutlined, ControlOutlined, WifiOutlined,
  SettingOutlined, HistoryOutlined, SendOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import { getRoleBasedTheme } from '../utils/rolePermissions';
import {
  DeviceSelector,
  ControlPanel,
  ConfigPanel,
  BulkProgressPanel,
  CommandHistoryTable
} from '../components/MQTTControl';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

/**
 * MQTTControl Page - ESP32 Control & Config Management
 *
 * Features:
 * - Single device and bulk operation modes
 * - Dynamic control commands from registry
 * - Dynamic config forms from registry
 * - Real-time batch progress via WebSocket
 * - Command history with filtering
 */
const MQTTControl = () => {
  // Auth & Theme
  const { user } = useAuth();
  const theme = getRoleBasedTheme(user?.role);

  // Device Selection
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);

  // MQTT Topics
  const [mqttTopics, setMqttTopics] = useState([]);

  // Bulk Operations
  const [activeBatch, setActiveBatch] = useState(null);

  // Device Response (from WebSocket)
  const [lastDeviceResponse, setLastDeviceResponse] = useState(null);

  // Sent command log (from ControlPanel/ConfigPanel callbacks)
  const [lastSentCommand, setLastSentCommand] = useState(null);

  // WebSocket
  const wsRef = useRef(null);

  useEffect(() => {
    fetchMqttTopics();
    setupWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const setupWebSocket = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.warn('WebSocket: Token yok, bağlantı kurulamıyor');
        return;
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws?token=${token}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        logger.info('WebSocket connected for MQTT Control');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle command responses
          // Backend format: { type, device_id, response: {...}, timestamp }
          // veya: { type, data: {...} } (timeout için)
          if (data.type === 'control_response') {
            const resp = data.response || data.data || {};
            // response_data string olarak gelebilir
            if (typeof resp.response_data === 'string') { try { resp.response_data = JSON.parse(resp.response_data); } catch(e) {} }
            setLastDeviceResponse({ type: 'control_response', ...resp, received_at: new Date().toISOString() });
          }

          if (data.type === 'config_response') {
            const resp = data.response || data.data || {};
            if (typeof resp.response_data === 'string') { try { resp.response_data = JSON.parse(resp.response_data); } catch(e) {} }
            setLastDeviceResponse({ type: 'config_response', ...resp, received_at: new Date().toISOString() });
          }

          if (data.type === 'command_timeout') {
            const resp = data.data || data.response || {};
            setLastDeviceResponse({ type: 'command_timeout', ...resp, received_at: new Date().toISOString() });
          }

          // Handle batch events
          if (data.type === 'batch_completed' && activeBatch?.batch_id === data.batch_id) {
            message.success('Batch işlemi tamamlandı!');
          }

          if (data.type === 'batch_cancelled' && activeBatch?.batch_id === data.batch_id) {
            message.warning('Batch işlemi iptal edildi');
          }
        } catch (e) {
          logger.error('WebSocket message parse error:', e);
        }
      };

      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        logger.info('WebSocket disconnected');
        // Reconnect after 5 seconds
        setTimeout(setupWebSocket, 5000);
      };

      wsRef.current = ws;
    } catch (error) {
      logger.error('WebSocket setup error:', error);
    }
  };

  const fetchMqttTopics = async () => {
    try {
      const response = await axios.get('/api/v1/mqtt/topics');
      setMqttTopics(response.data.topics || []);
    } catch (error) {
      logger.error('MQTT topics fetch error:', error);
      setMqttTopics([]);
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'single') {
      setSelectedDevices([]);
    } else {
      setSelectedDevice(null);
    }
  };

  const handleBulkStart = (batchData) => {
    setActiveBatch(batchData);
  };

  const handleBatchComplete = () => {
    // Keep batch visible for a while, then clear
    setTimeout(() => setActiveBatch(null), 5000);
  };

  // Yanıt verisi etiketleri
  const FL = {
    uptime:'Çalışma', heap:'Bellek', rssi:'Sinyal', locked:'Kilitli',
    interval:'Periyot', interval_ms:'Periyot', total_shots:'Atış',
    timeout:'Timeout', warning:'Uyarı', enabled:'Aktif',
    last_online:'Son Online', rtc_tamper:'RTC', delay_seconds:'Gecikme',
    rebooting:'Reboot', saved:'Kayıt', ssid:'WiFi', broker:'Broker',
    port:'Port', model:'Model', sn:'Seri No', ver:'FW',
    head:'Başlık', hd:'Başlık', flow:'Akış', fl:'Akış',
    water_temp:'Su Sıc.', wt:'Su Sıc.', ambient_temp:'Ortam', at:'Ortam',
    laser_comm:'Lazer', lc:'Lazer', wifi:'WiFi', mqtt:'MQTT',
    wt_r:'Su Sıc. Limit', at_r:'Ortam Limit', fl_r:'Akış Limit',
    s:'Serial', m:'MQTT', d:'SD', e:'Hata', o:'OTA', y:'Sistem',
    c:'Kontrol', f:'Config', l:'Seviye',
    error:'Hata', session:'Seans', ota:'OTA', system:'Sistem',
    control:'Kontrol', config:'Config', level:'Seviye',
    serial:'Serial', sd:'SD Kart',
    log:'Log', safety:'Güvenlik',
  };
  // Boolean olarak bilinen key'ler
  const BOOL_KEYS = new Set(['e','s','o','y','c','f','hd','fl','wt','at','lc',
    'error','session','ota','system','control','config',
    'head','flow','water_temp','ambient_temp','laser_comm',
    'head_check','flow_check','water_temp_check','ambient_temp_check','laser_comm_check',
    'wifi_check','mqtt_check','wifi','mqtt',
    'locked','enabled','offline_enabled','rtc_tamper','rebooting','saved']);

  const fmtVal = (k, v) => {
    if (v === true) return <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Aktif</Tag>;
    if (v === false) return <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Pasif</Tag>;
    // 0/1 değerler — bilinen boolean key'ler için
    if ((v === 1 || v === 0) && BOOL_KEYS.has(k)) {
      return v === 1 ? <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Aktif</Tag> : <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Pasif</Tag>;
    }
    if (Array.isArray(v)) return v.join(' ~ ') + (k.includes('t_r') ? ' °C' : k.includes('fl_r') ? ' L/dk' : '');
    if ((k === 'l' || k === 'level') && typeof v === 'number') return <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{['WARN','INFO','DEBUG'][v] || v}</Tag>;
    if (k === 'uptime') { const h = Math.floor(v/3600); const m = Math.floor((v%3600)/60); return `${h}s ${m}dk`; }
    if (k === 'heap') return `${(v/1024).toFixed(0)} KB`;
    if (k === 'rssi') return `${v} dBm`;
    if (k === 'interval') return `${v} sn`;
    if (k === 'interval_ms') return `${v} ms`;
    if (k === 'last_online' && v > 1e9) return new Date(v*1000).toLocaleString('tr-TR');
    if (k === 'timeout' || k === 'warning' || k.includes('_hrs')) return `${v} sa`;
    if (k === 'delay_seconds') return `${v} sn`;
    if (k === 'total_shots') return v.toLocaleString('tr-TR');
    return String(v);
  };
  const renderResponseSummary = (data) => {
    if (!data || typeof data !== 'object') return null;
    const lines = [];
    const renderObj = (obj, prefix, depth = 0) => {
      for (const [k, v] of Object.entries(obj)) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'object' && !Array.isArray(v)) {
          lines.push(
            <div key={prefix+k} style={{ marginTop: depth === 0 ? 6 : 2, paddingTop: depth === 0 ? 4 : 0, borderTop: depth === 0 ? '1px solid #e8e8e8' : 'none' }}>
              <Text strong style={{ fontSize: 11, color: '#1890ff' }}>{FL[k]||k}</Text>
            </div>
          );
          renderObj(v, prefix+k+'.', depth+1);
        } else {
          lines.push(
            <div key={prefix+k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', paddingLeft: depth > 1 ? 8 : 0 }}>
              <Text style={{ fontSize: 11, color: '#666' }}>{FL[k]||k}</Text>
              <span style={{ fontSize: 11 }}>{fmtVal(k, v)}</span>
            </div>
          );
        }
      }
    };
    renderObj(data, '');
    return <div>{lines}</div>;
  };

  // Sağ panel: cihaz + gönderilen + yanıt TEK kartta
  const renderRightPanel = () => {
    if (!selectedDevice) {
      return <Alert message="Cihaz Seçilmedi" description="Yukarıdan bir cihaz seçin." type="info" showIcon />;
    }

    return (
      <Card size="small" bodyStyle={{ padding: 12 }}>
        {/* Cihaz Bilgisi — kompakt tek satır */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
          <Space size={4}>
            <Badge status={selectedDevice.status === 'online' ? 'success' : 'error'} />
            <Text strong style={{ fontSize: 13 }}>{selectedDevice.name}</Text>
          </Space>
          <Space size={8}>
            <Text code style={{ fontSize: 10 }}>{selectedDevice.mac_address}</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>FW: {selectedDevice.firmware_version || '?'}</Text>
          </Space>
        </div>

        {/* Son Gönderilen — minimal */}
        {lastSentCommand && (
          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space size={4}>
                <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{lastSentCommand.type === 'control' ? 'CTRL' : 'CFG'}</Tag>
                <Text strong style={{ fontSize: 12 }}>{lastSentCommand.command || lastSentCommand.config_type}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 10 }}>{lastSentCommand.sent_at && new Date(lastSentCommand.sent_at).toLocaleTimeString('tr-TR')}</Text>
            </div>
          </div>
        )}

        {/* Son Gelen Yanıt */}
        {lastDeviceResponse && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Space size={4}>
                <Tag
                  color={lastDeviceResponse.type === 'command_timeout' ? 'orange' : lastDeviceResponse.status === 'success' ? 'green' : 'red'}
                  style={{ margin: 0, fontSize: 10 }}
                >
                  {lastDeviceResponse.type === 'command_timeout' ? 'TIMEOUT' : (lastDeviceResponse.status || '?').toUpperCase()}
                </Tag>
                <Text strong style={{ fontSize: 12 }}>{lastDeviceResponse.command || lastDeviceResponse.config_type}</Text>
                {lastDeviceResponse.status_message && <Text type="secondary" style={{ fontSize: 11 }}>— {lastDeviceResponse.status_message}</Text>}
              </Space>
              <Text type="secondary" style={{ fontSize: 10 }}>{lastDeviceResponse.received_at && new Date(lastDeviceResponse.received_at).toLocaleTimeString('tr-TR')}</Text>
            </div>

            {lastDeviceResponse.response_data && Object.keys(lastDeviceResponse.response_data).length > 0 && (
              <div style={{ background: '#f6f8fa', padding: 8, borderRadius: 4, maxHeight: 400, overflow: 'auto' }}>
                {lastDeviceResponse.response_data._truncated ? (
                  <div>
                    <Tag color="orange" style={{ marginBottom: 4, fontSize: 10 }}>Veri kesilmiş (firmware payload limiti)</Tag>
                    <pre style={{ margin: 0, fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{lastDeviceResponse.response_data._raw}</pre>
                  </div>
                ) : (
                  renderResponseSummary(lastDeviceResponse.response_data)
                )}
                <details style={{ marginTop: 4 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 10, color: '#bbb' }}>Raw JSON</summary>
                  <pre style={{ margin: '2px 0 0', fontSize: 9, maxHeight: 120, overflow: 'auto', background: '#f0f0f0', padding: 4, borderRadius: 4 }}>
                    {JSON.stringify(lastDeviceResponse.response_data, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {lastDeviceResponse.type === 'command_timeout' && (
              <Text type="warning" style={{ fontSize: 11 }}>Cihaz {lastDeviceResponse.timeout_seconds || 30}s içinde yanıt vermedi.</Text>
            )}
          </div>
        )}

        {/* Yanıt yoksa bilgi */}
        {!lastDeviceResponse && !lastSentCommand && (
          <Text type="secondary" style={{ fontSize: 11 }}>Komut gönderildikten sonra yanıt burada gösterilecek.</Text>
        )}
      </Card>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <ControlOutlined style={{ marginRight: 8 }} />
          MQTT Control Center
        </Title>
      </div>

      {/* Action Buttons */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchMqttTopics}
            >
              Yenile
            </Button>
          </Space>
        </div>
      </Card>

      {/* Device Selector */}
      <DeviceSelector
        mode={mode}
        selectedDevice={selectedDevice}
        selectedDevices={selectedDevices}
        onSelectDevice={setSelectedDevice}
        onSelectDevices={setSelectedDevices}
        onModeChange={handleModeChange}
      />

      {/* Active Batch Progress (when bulk operation is running) */}
      {activeBatch && (
        <div style={{ marginBottom: 24 }}>
          <BulkProgressPanel
            batchId={activeBatch.batch_id}
            wsConnection={wsRef.current}
            onCancel={() => setActiveBatch(null)}
            onComplete={handleBatchComplete}
          />
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultActiveKey="control">
        <TabPane
          tab={
            <span>
              <SendOutlined />
              Control Komutları
            </span>
          }
          key="control"
        >
          <Row gutter={24}>
            <Col span={mode === 'single' ? 16 : 24}>
              <ControlPanel
                selectedDevice={selectedDevice}
                mode={mode}
                selectedDevices={selectedDevices}
                onBulkCommandStart={handleBulkStart}
                lastDeviceResponse={lastDeviceResponse}
                onCommandSent={(data) => setLastSentCommand({ type: 'control', ...data })}
              />
            </Col>
            {mode === 'single' && (
              <Col span={8}>
                {renderRightPanel()}
              </Col>
            )}
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SettingOutlined />
              Config Güncellemeleri
            </span>
          }
          key="config"
        >
          <Row gutter={24}>
            <Col span={mode === 'single' ? 16 : 24}>
              <ConfigPanel
                selectedDevice={selectedDevice}
                mode={mode}
                selectedDevices={selectedDevices}
                onBulkConfigStart={handleBulkStart}
                lastDeviceResponse={lastDeviceResponse}
                onConfigSent={(data) => setLastSentCommand({ type: 'config', ...data })}
              />
            </Col>
            {mode === 'single' && (
              <Col span={8}>
                {renderRightPanel()}
              </Col>
            )}
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <WifiOutlined />
              MQTT Topic'leri
            </span>
          }
          key="topics"
        >
          <Card title="MQTT Topic Yapısı">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Topic</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Açıklama</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Yön</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>QoS</th>
                </tr>
              </thead>
              <tbody>
                {mqttTopics.map((topic, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px' }}>
                      <Text code style={{ fontSize: '11px' }}>{topic.topic}</Text>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Text style={{ fontSize: '12px' }}>{topic.description}</Text>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Tag color={topic.direction?.includes('→') ? 'blue' : 'green'} size="small">
                        {topic.direction}
                      </Tag>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Tag color="orange" size="small">{topic.qos}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <HistoryOutlined />
              Komut Geçmişi
            </span>
          }
          key="history"
        >
          <CommandHistoryTable
            deviceId={mode === 'single' && selectedDevice ? selectedDevice.id : null}
            autoRefresh={true}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default MQTTControl;
